import React, { useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useWatch } from "react-hook-form";
import { remove } from "remove-accents";
import pDebounce from "p-debounce";
import Autocomplete from "./Autocomplete";
import CacheMap from "../../lib/form/CacheMap";

const cache = new CacheMap("fetch_company");

function Company({
  methods,
  label,
  name,
  companyName,
  companyAddress,
  companyZipcode,
  sirenName,
  dependOfCity,
  required,
  variant,
}) {
  const { control, setValue, register } = methods;

  const dependOfValue = useWatch({
    control,
    name: dependOfCity,
  });

  useEffect(() => {
    if (dependOfCity && dependOfValue) {
      setValue(name, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependOfCity, dependOfValue]);

  const fetchData = () => {
    return pDebounce(async (inputValue, value, setOptions, setLoading) => {
      if (inputValue === "") {
        setOptions(value ? [value] : []);
        return undefined;
      }

      const query = encodeURIComponent(remove(inputValue).toLowerCase());
      const url = dependOfCity ? `https://sirene-as-api.timeonegroup.com/autocomplete-city/?nom_complet=${query}&code_insee=${dependOfValue}` : `https://sirene-as-api.timeonegroup.com/autocomplete/?q=${query}`;

      setLoading(true);

      let result;
      if (cache.has(url)) {
        result = cache.get(url);
      } else {
        const response = await fetch(
          url,
          {
            method: "GET",
            headers: {},
            mode: "cors",
            cache: "default",
          }
        );
        result = await response.json();
        cache.set(url, result);
      }

      setLoading(false);

      setOptions(result);
    }, 500);
  };

  const buildCompanyName = (option) => option.nom_raison_sociale || `${option.prenom} ${option.nom}`;

  const buildDisplayCompanyName = (option) => {
    const baseName = buildCompanyName(option);

    const subNames = [
      option.denominations_usuelles,
      option.nom_usage,
      option.sigle,
    ].flatMap(value => value).filter(value => value !== undefined);

    return baseName.concat('', subNames.length > 0 ? ` (${subNames.join(',')})` : '');
  }

  return (
    <React.Fragment>
      <Autocomplete
        name={name}
        label={label}
        required={required}
        variant={variant}
        methods={methods}
        disabled={(dependOfCity && !dependOfValue)}
        onChange={(event, newValue) => {
          setValue(
            companyName,
            newValue?.nom_raison_sociale ||
            `${newValue?.prenom} ${newValue?.nom}`
          );
          setValue(sirenName, newValue?.siren);
          setValue(companyAddress, newValue?.geo_adresse);
          setValue(companyZipcode, newValue?.code_postal);
        }}
        isOptionEqualToValue={(option, value) => option.siren === value.siren}
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : buildCompanyName(option)
        }
        renderOption={(props, option) => (
          <li {...props} key={`li_company_${option.siren}${props.key}`}>
            <Box key={`box_company_${option.siren}${props.key}`} m={0}>
              <span
                key={`company_${option.siren}${props.key}`}
                style={{ fontWeight: 400 }}
              >
                {buildDisplayCompanyName(option)}
              </span>
              <Typography
                key={`typo_company_${option.siren}${props.key}`}
                variant="body2"
                color="textSecondary"
              >
                SIREN : {option.siren}, Siège : {option.geo_adresse}
              </Typography>
            </Box>
          </li>
        )}
        dataLoader={fetchData()}
      />
      <input {...register(companyName)} type="hidden" />
      <input {...register(sirenName)} type="hidden" />
    </React.Fragment>
  );
}

Company.defaultProps = {
  label: "Votre société",
  name: "autocomplete_company",
  companyName: "company_name",
  sirenName: "siren",
  required: "Merci de saisir votre société",
  variant: "outlined",
  companyAddress: "address",
  companyZipcode: "company_zipcode"
};

Company.propTypes = {
  /**
   * methods du useForm()
   */
  methods: PropTypes.instanceOf(Object),
  /**
   * Label du champ
   */
  label: PropTypes.string,
  /**
   * Name du tag HTML input
   */
  name: PropTypes.string,
  /**
   * Name du tag HTML input contenant le nom de l'entreprise
   */
  companyName: PropTypes.string,
  /**
   * Name du tag HTML input contenant l'adresse de l'entreprise
   */
  companyAddress: PropTypes.string,
  /**
   * Name du tag HTML input contenant le code postal de l'entreprise
   */
  companyZipcode: PropTypes.string,
  /**
   * Name du tag HTML input contenant le SIREN
   */
  sirenName: PropTypes.string,
  /**
   * Nom du champ City de dépendance dans le formulaire
   */
  dependOfCity: PropTypes.string,
  /**
   * Message d'erreur si le champ est requis
   */
  required: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * The variant to use
   * 'filled'
   * | 'outlined'
   * | 'standard'
   */
  variant: PropTypes.string,
};

export default Company;
